import React, { useId, useMemo } from "react";
import {
    Area,
    AreaChart,
    ResponsiveContainer,
    usePlotArea,
    XAxis,
    YAxis,
} from "recharts";
import { colors } from "../../theme/colors";

function normalizeLeaderboardElo(value) {
    if (typeof value === "string" && value.trim() !== "") {
        const n = Number.parseInt(value, 10);
        return Number.isFinite(n) ? n : null;
    }
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
}

export function buildEloSparkPoints(matchesNewestFirst, currentEloRaw) {
    const slice = (matchesNewestFirst || []).slice(0, 5);
    if (slice.length === 0) return [];

    const chrono = [...slice].reverse();
    const deltas = slice.map((m) => {
        const d = Number(m?.eloChange);
        return Number.isFinite(d) ? d : 0;
    });

    const current = normalizeLeaderboardElo(currentEloRaw);

    let elosChrono;

    if (current == null) {
        let acc = 0;
        elosChrono = chrono.map((m) => {
            const d = Number(m?.eloChange);
            const delta = Number.isFinite(d) ? d : 0;
            acc += delta;
            return acc;
        });
    } else {
        const postsNewestFirst = [];
        let post = current;
        for (let i = 0; i < slice.length; i++) {
            postsNewestFirst.push(post);
            post -= deltas[i];
        }
        elosChrono = postsNewestFirst.reverse();
        if (slice.length === 1) {
            elosChrono = [current - deltas[0], current];
        }
    }

    const metaForMatch = (m) => {
        const d = Number(m?.eloChange);
        const change = Number.isFinite(d) ? d : 0;
        let result = "—";
        if (m?.result === "win") result = "W";
        else if (m?.result === "loss") result = "L";
        return { change, result };
    };

    const zip = (elos) =>
        chrono.map((m, i) => {
            const { change, result } = metaForMatch(m);
            return {
                elo: elos[i],
                change,
                result,
            };
        });

    if (elosChrono.length === chrono.length) {
        return zip(elosChrono);
    }

    if (elosChrono.length === 2 && chrono.length === 1) {
        const { change, result } = metaForMatch(chrono[0]);
        return [
            { elo: elosChrono[0], change, result },
            { elo: elosChrono[1], change, result },
        ];
    }

    return elosChrono.map((elo, i) => ({
        elo,
        ...(i < chrono.length ? metaForMatch(chrono[i]) : { change: 0, result: "—" }),
    }));
}

export function buildWeeklySparkPoints(eloHistoryWeek) {
    return (eloHistoryWeek || []).map((entry) => {
        const change = Number(entry?.change) || 0;
        return {
            elo: entry?.elo,
            change,
            result: change > 0 ? "W" : change < 0 ? "L" : "—",
        };
    });
}

export function padSparkChartPoints(points) {
    if (points.length === 0) return [];
    if (points.length === 1) return [points[0], points[0]];
    return points;
}

function SparkColoredSegmentLines({
    chartData,
    strokeColor,
    strokeWidth = 2,
    monochrome = false,
    yMin,
    yMax,
}) {
    const plotArea = usePlotArea();
    if (!chartData?.length || !plotArea) {
        return null;
    }

    const xDomainMax = Math.max(chartData.length - 1, 1);
    const yRange = yMax - yMin || 1;
    const xScale = (value) => plotArea.x + (Number(value) / xDomainMax) * plotArea.width;
    const yScale = (value) => plotArea.y + ((yMax - Number(value)) / yRange) * plotArea.height;
    const neutralStroke = strokeColor || colors.text.muted;
    const monoStroke = strokeColor || colors.primary.main;

    const elements = [];
    for (let i = 1; i < chartData.length; i++) {
        const p0 = chartData[i - 1];
        const p1 = chartData[i];
        const isWin = p0.result === "W";
        const stroke = monochrome
            ? monoStroke
            : isWin
              ? colors.state.success.main
              : p0.result === "L"
                ? colors.state.danger.main
                : neutralStroke;
        const x1 = xScale(p0.idx);
        const y1 = yScale(p0.elo);
        const x2 = xScale(p1.idx);
        const y2 = yScale(p1.elo);
        if (
            [x1, y1, x2, y2].some(
                (v) => v == null || (typeof v === "number" && Number.isNaN(v))
            )
        ) {
            continue;
        }
        elements.push(
            <line
                key={`elo-seg-${String(p0.idx)}-${String(p1.idx)}`}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke={stroke}
                strokeWidth={strokeWidth}
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        );
    }

    return <g>{elements}</g>;
}

export default function EloSparklineChart({
    points,
    strokeColor,
    curveType = "linear",
    chartMargin = { top: 6, right: 8, left: 8, bottom: 6 },
    yPadRatio = 0.12,
    segmentStrokeWidth = 2,
    monochrome = false,
}) {
    const rawId = useId().replace(/:/g, "");
    const gradId = `elo-spark-grad-${rawId}`;
    const lineColor = monochrome ? colors.primary.main : strokeColor;

    const chartData = useMemo(() => {
        const rows = points.map((p, idx) => ({
            idx,
            elo: p.elo,
            change: p.change,
            result: p.result,
            eloWin: null,
            eloLoss: null,
        }));
        for (let i = 1; i < points.length; i++) {
            const key = points[i - 1].result === "W" ? "eloWin" : "eloLoss";
            rows[i - 1][key] = points[i - 1].elo;
            rows[i][key] = points[i].elo;
        }
        return rows;
    }, [points]);

    const [yMin, yMax] = useMemo(() => {
        if (chartData.length === 0) return [0, 1];
        const nums = chartData.map((d) => d.elo);
        const min = Math.min(...nums);
        const max = Math.max(...nums);
        const pad =
            min === max ? 4 : Math.max(1, Math.round((max - min) * yPadRatio));
        return [min - pad, max + pad];
    }, [chartData, yPadRatio]);

    return (
        <ResponsiveContainer width="100%" height="100%" style={{ pointerEvents: "none" }}>
            <AreaChart
                data={chartData}
                margin={chartMargin}
            >
                <defs>
                    {monochrome ? (
                        <linearGradient
                            id={`${gradId}-primary`}
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                        >
                            <stop
                                offset="0%"
                                stopColor={colors.primary.main}
                                stopOpacity={0.22}
                            />
                            <stop
                                offset="100%"
                                stopColor={colors.primary.main}
                                stopOpacity={0}
                            />
                        </linearGradient>
                    ) : (
                        <>
                            <linearGradient
                                id={`${gradId}-win`}
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="1"
                            >
                                <stop
                                    offset="0%"
                                    stopColor={colors.state.success.main}
                                    stopOpacity={0.22}
                                />
                                <stop
                                    offset="100%"
                                    stopColor={colors.state.success.main}
                                    stopOpacity={0}
                                />
                            </linearGradient>
                            <linearGradient
                                id={`${gradId}-loss`}
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="1"
                            >
                                <stop
                                    offset="0%"
                                    stopColor={colors.state.danger.main}
                                    stopOpacity={0.22}
                                />
                                <stop
                                    offset="100%"
                                    stopColor={colors.state.danger.main}
                                    stopOpacity={0}
                                />
                            </linearGradient>
                        </>
                    )}
                </defs>
                <XAxis
                    dataKey="idx"
                    type="number"
                    domain={[0, Math.max(chartData.length - 1, 1)]}
                    hide
                />
                <YAxis hide domain={[yMin, yMax]} />
                {monochrome ? (
                    <Area
                        type={curveType}
                        dataKey="elo"
                        stroke="none"
                        strokeWidth={0}
                        fill={`url(#${gradId}-primary)`}
                        baseLine={yMin}
                        dot={false}
                        activeDot={false}
                        isAnimationActive={false}
                    />
                ) : (
                    <>
                        <Area
                            type={curveType}
                            dataKey="eloLoss"
                            stroke="none"
                            strokeWidth={0}
                            fill={`url(#${gradId}-loss)`}
                            baseLine={yMin}
                            dot={false}
                            activeDot={false}
                            isAnimationActive={false}
                        />
                        <Area
                            type={curveType}
                            dataKey="eloWin"
                            stroke="none"
                            strokeWidth={0}
                            fill={`url(#${gradId}-win)`}
                            baseLine={yMin}
                            dot={false}
                            activeDot={false}
                            isAnimationActive={false}
                        />
                    </>
                )}
                <SparkColoredSegmentLines
                    chartData={chartData}
                    strokeColor={lineColor}
                    strokeWidth={segmentStrokeWidth}
                    monochrome={monochrome}
                    yMin={yMin}
                    yMax={yMax}
                />
            </AreaChart>
        </ResponsiveContainer>
    );
}
